import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import type { ExtendTarget } from '../App';
import { api, type Plan } from '../api/client';
import { T } from '../styles/tokens';
import { haptic, hapticSuccess, tg } from '../telegram/sdk';

type Proto = 'wireguard' | 'vless';

interface Props {
  extend?: ExtendTarget | null;
  onDone?: () => void;
}

export default function Purchase({ extend = null, onDone }: Props) {
  const isExtend = !!extend;
  const [proto, setProto] = useState<Proto>(extend?.protocol ?? 'wireguard');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selected, setSelected] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (extend) setProto(extend.protocol);
  }, [extend]);

  useEffect(() => {
    api.plans.list().then(setPlans).catch(console.error);
  }, []);

  useEffect(() => {
    const mb = tg()?.MainButton;
    if (!mb) return;
    if (!selected) {
      mb.hide();
      return;
    }

    mb.setText(`${isExtend ? 'Продлить' : 'Оплатить'} ${selected.price} ₽`);
    mb.show();

    const pollOrderUntilPaid = async (orderId: string) => {
      // Telegram gives us the ``paid`` callback as soon as the user confirms
      // payment — but the bot needs a moment to receive the
      // ``successful_payment`` update and create the subscription. We poll
      // our own API for up to ~30s to surface a confirmation to the user.
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const status = await api.payments.get(orderId);
          if (status.status === 'paid') {
            hapticSuccess();
            setInfo(
              isExtend
                ? 'Подписка продлена! Откройте вкладку «Подписки», чтобы увидеть новую дату.'
                : 'Подписка активирована! Конфиг доступен на вкладке «Подписки».',
            );
            setTimeout(() => onDone?.(), 1200);
            return;
          }
          if (status.status === 'failed') {
            setInfo('Оплата не прошла. Попробуйте ещё раз.');
            return;
          }
        } catch {
          // keep polling — likely a transient error
        }
      }
      setInfo('Оплата принята, ожидаем активации… Обновите «Подписки» через минуту.');
    };

    const handler = async () => {
      const tgWeb = tg();
      haptic('medium');
      setLoading(true);
      setInfo(null);
      mb.showProgress();
      try {
        const invoice = await api.payments.createTmaInvoice({
          plan_code: selected.code,
          protocol: proto,
          device_label: extend?.deviceLabel ?? undefined,
          target_subscription_id: extend?.subscriptionId,
        });

        if (!tgWeb?.openInvoice) {
          // Extremely unlikely inside Telegram, but fall back to openLink.
          tgWeb?.openLink(invoice.invoice_link);
          setInfo('Откройте Telegram для завершения оплаты.');
          return;
        }

        tgWeb.openInvoice(invoice.invoice_link, async (status) => {
          if (status === 'paid') {
            setInfo('Оплата получена. Активируем подписку…');
            await pollOrderUntilPaid(invoice.order_id);
            setSelected(null);
          } else if (status === 'cancelled') {
            setInfo('Оплата отменена.');
          } else if (status === 'failed') {
            setInfo('Оплата не прошла. Попробуйте ещё раз.');
          } else {
            setInfo('Оплата в обработке — обновите «Подписки» через минуту.');
          }
        });
      } catch (e) {
        setInfo(e instanceof Error ? e.message : 'Ошибка платежа');
      } finally {
        mb.hideProgress();
        setLoading(false);
      }
    };
    mb.onClick(handler);
    return () => mb.offClick(handler);
  }, [selected, proto, isExtend, extend?.subscriptionId, extend?.deviceLabel, onDone]);

  return (
    <div style={p.wrap}>
      <div style={p.header}>
        <div style={p.headTag}>// {isExtend ? `продление #${extend?.subscriptionId}` : 'новая подписка'}</div>
        <div style={p.headTitle}>{isExtend ? 'Продлить' : 'Купить'}</div>
      </div>

      <div style={p.section}>
        <div style={p.label}>// протокол{isExtend ? ' (фиксирован)' : ''}</div>
        <div style={p.protoRow}>
          {(['wireguard', 'vless'] as Proto[]).map((pr) => {
            const disabled = isExtend && pr !== proto;
            return (
              <button
                key={pr}
                style={{
                  ...p.protoBtn,
                  ...(proto === pr ? p.protoBtnActive : {}),
                  ...(disabled ? p.protoBtnDisabled : {}),
                }}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  haptic('light');
                  setProto(pr);
                }}
              >
                <div style={p.protoBtnName}>{pr === 'wireguard' ? 'WireGuard' : 'VLESS Reality'}</div>
                <div style={p.protoBtnDesc}>{pr === 'wireguard' ? 'Скорость' : 'Обход DPI'}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={p.section}>
        <div style={p.label}>// тариф</div>
        {plans.map((pl) => (
          <button
            key={pl.code}
            style={{ ...p.planBtn, ...(selected?.code === pl.code ? p.planBtnActive : {}) }}
            onClick={() => {
              haptic('light');
              setSelected(pl);
            }}
          >
            <div style={p.planLeft}>
              <div style={p.planPer}>
                {pl.label}
                {pl.popular && <span style={p.hotBadge}>популярный</span>}
              </div>
              <div style={p.planDay}>{pl.per_day} ₽/день</div>
            </div>
            <div style={p.planRight}>
              <span style={p.planPrice}>{pl.price}</span>
              <span style={p.planCur}> ₽</span>
            </div>
          </button>
        ))}
      </div>

      {!selected && <div style={p.hint}>Выберите тариф — кнопка оплаты появится снизу</div>}
      {loading && <div style={p.hint}>Обработка платежа...</div>}
      {info && <div style={p.info}>{info}</div>}
    </div>
  );
}

const p: Record<string, CSSProperties> = {
  wrap: { padding: '20px 16px 24px' },
  header: { marginBottom: 18 },
  headTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  headTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em' },
  section: { marginBottom: 20 },
  label: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 },
  protoRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  protoBtn: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 12px', cursor: 'pointer', textAlign: 'left', fontFamily: 'DM Sans, sans-serif', color: T.text },
  protoBtnActive: { background: T.accentSoft, borderColor: T.accent },
  protoBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  protoBtnName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, marginBottom: 2 },
  protoBtnDesc: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' },
  planBtn: { width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', color: T.text },
  planBtnActive: { background: T.accentSoft, borderColor: T.accent },
  planLeft: { textAlign: 'left' },
  planPer: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: T.text },
  hotBadge: { marginLeft: 8, fontFamily: 'DM Mono, monospace', fontSize: 8, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentMid}`, padding: '1px 6px', borderRadius: 2, textTransform: 'uppercase', letterSpacing: '0.06em', verticalAlign: 'middle' },
  planDay: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, marginTop: 2 },
  planRight: {},
  planPrice: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: T.text },
  planCur: { fontSize: 13, color: T.textSec },
  hint: { textAlign: 'center', fontSize: 12, color: T.textMuted, padding: '10px 0', fontFamily: 'DM Mono, monospace', letterSpacing: '0.04em' },
  info: { marginTop: 10, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12, color: T.textSec, fontFamily: 'DM Mono, monospace', lineHeight: 1.6 },
};
